import { Injectable } from '@angular/core';
import { BehaviorSubject, interval, map, Observable, switchMap, tap } from 'rxjs';
import { API_ENDPOINTS } from 'src/app/config/api-endpoints';
import {
  RefreshStatusDto,
  RefreshStatusRequestDto,
  RefreshStatusResponseDto,
  SourceSheet,
} from 'src/app/model';
import { environment } from 'src/environments/environment';
import { HttpService } from '../http-service/http.service';
import { contextToParams, rgContextToParams } from 'src/app/utils';

@Injectable({ providedIn: 'root' })
export class RefreshStatusService {
  // 1) A private BehaviorSubject that holds the current array of status-rows
  private _statusMap = new BehaviorSubject<Map<string, RefreshStatusDto>>(
    new Map()
  );
  private pollingSubscription: any;
  // 2) A public, read-only Observable that components can subscribe to
  public readonly statuses$ = this._statusMap
    .asObservable()
    .pipe(map((m) => Array.from(m.values())));

  constructor(private http: HttpService) {}

  /**
   * Call the API with the given request DTO, push the returned Data[]
   * into the BehaviorSubject, and return the full response observable.
   */
  refresh(req: RefreshStatusRequestDto): Observable<RefreshStatusResponseDto> {
    const isRG = !!(req).reportingGroupId;
    const endpoint = isRG
      ? API_ENDPOINTS.common.GetRefreshStatusForRG
      : API_ENDPOINTS.common.GetRefreshStatus;
    const url = `${environment.apiBaseURL}${endpoint}`;

    let params = isRG ? rgContextToParams(req) : contextToParams(req);
    req.Sheets.forEach((sheetEnum) => {
      const sheetName = SourceSheet[sheetEnum] ?? sheetEnum.toString();
      params = params.append('Sheets', sheetName);
    });

    return this.http.get<RefreshStatusResponseDto>(url, params).pipe(
      tap((resp) => {
        if (!resp.Success || !resp.Data) return;

        // clone old map
        const next = new Map(this._statusMap.getValue());

        // upsert each item by its unique key
        resp.Data.forEach((item) => {
          next.set(item.SourceSheet, item);
        });

        // push the new map
        this._statusMap.next(next);
      })
    );
  }
   /**
   * Start polling the API at regular intervals.
   */
   startPolling(request: RefreshStatusRequestDto, intervalMs: number = 30000): void {
    this.stopPolling(); // Ensure no duplicate polling is running

    this.pollingSubscription = interval(intervalMs)
      .pipe(switchMap(() => this.refresh(request))) // Call the refresh method at each interval
      .subscribe({
        error: (err) => console.error('Polling error:', err),
      });
  }
  /**
   * Stop polling the API.
   */
  stopPolling(): void {
    if (this.pollingSubscription) {
      this.pollingSubscription.unsubscribe();
      this.pollingSubscription = null;
    }
  }
}
